/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryProviderAPI',
	'../../../filters/FilterQuerySpec',
	'../../../apiHelpers/SlotAPIHelper',
	'./QueryDataItemBuilder',
	'./DataQueryUtils',
	'../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../../../widgets/livewidget/query/CommonQueryBuilder',
	'underscore'
], (APIFactory, QueryProviderAPI, FilterQuerySpec, SlotAPIHelper,
	QueryDataItemBuilder, DataQueryUtils, BrowserUtils, CommonQueryBuilder, _) => {
	class QueryProvider {
		static DEFAULT_ROWLIMIT = 3000;
		static SUPPRESSION_NONE = 'none';

		constructor({ content, dashboardAPI }) {
			this.content = content;
			this.dashboard = dashboardAPI;
			this.content.getFeature('DataQueryExecution').registerQueryProvider(this.getAPI());
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryProviderAPI]);
			}
			return this.api;
		}

		getType() {
			return 'main';
		}

		/**
		 * @returns a list of query specs.
		 */
		getQuerySpecList() {
			const visualization = this.content.getFeature('Visualization');
			const dataSource = visualization.getDataSource();
			const dataSourceId = dataSource && dataSource.getId();
			const mappedSlotsList = visualization.getSlots().getMappedSlotList();
			const layerToQuerySlotsMap = new Map();
			const mappingCompleteLayerList = this._getMappingCompleteLayers() || [];
			for(let i=0; i<mappedSlotsList.length; i++ ) {
				const slot = mappedSlotsList[i];
				if (this._canQuery(slot)){
					const layerIds = slot.getDefinition().getDatasetIdList();
					layerIds.forEach(layerId => {
						if (mappingCompleteLayerList.indexOf(layerId) === -1) {
							//skip the layer if the required slots for that layer are NOT filled
							return;
						}
						if (!layerToQuerySlotsMap.has(layerId)) {
							layerToQuerySlotsMap.set(layerId, []);
						}
						layerToQuerySlotsMap.get(layerId).push(slot);
					});
				}
			}

			const querySpecList = [];
			const mainQuerySpecList = this._getMainQuerySpecList(layerToQuerySlotsMap);
			querySpecList.push(...mainQuerySpecList);
			querySpecList.push(...this._getMinMaxQuerySpecList(layerToQuerySlotsMap, mainQuerySpecList));
			for( let j=0; j<querySpecList.length; j++ ){
				querySpecList[j].dataSourceId = dataSourceId;
			}

			return querySpecList;
		}

		_getMappingCompleteLayers() {
			const visualization = this.content.getFeature('Visualization');
			const datasetIds = visualization.getDefinition().getDatasetList().map(dataset => dataset.id);
			const slots = visualization.getSlots();
			return datasetIds.filter(id=>slots.isMappingComplete(id));
		}

		_getQuerySpecLimit() {
			//DEFAULT limit
			let dataRowLimit = QueryProvider.DEFAULT_ROWLIMIT;
			const definition = this.content.getFeature('Visualization').getDefinition();
			if (definition.getProperty('dataRowLimit')) {
				//Some charts have difficulty rendering a large set of rows, in this case, a smaller dataRowLimit is set in the definition.
				dataRowLimit = definition.getProperty('dataRowLimit');

				// Reduce data limit on IE
				if (BrowserUtils.isIE() && definition.getProperty('dataRowLimitIE')) {
					dataRowLimit = definition.getProperty('dataRowLimitIE');
				}
			}
			return dataRowLimit;
		}

		_getMainQuerySpecList(layerToQuerySlotsMap) {

			const queryDataItemBuilder = this._getQueryDataItemBuilder(layerToQuerySlotsMap);
			const querySpecList = [];
			const outputEdgeFilterExceptions = [];
			const queryHints = this._getQueryHints(layerToQuerySlotsMap);
			layerToQuerySlotsMap.forEach((querySlots, layerId)=>{
				const specObject = {
					id: layerId,
					type: 'main',
					spec: {
						version: '1',
						dataItems: [],
						projections: [],
						limit: this._getQuerySpecLimit()
					}
				};
				querySlots.forEach(slot=>{
					specObject.spec.projections.push(...this._getProjectionsForSlot(slot));
					specObject.spec.dataItems.push(...this._getDataItemsForSlot(slot, queryDataItemBuilder, outputEdgeFilterExceptions));
				});

				const excludeNonProjectedRangeFilters = DataQueryUtils.shouldExcludeNonProjectedRangeFilters(layerToQuerySlotsMap, layerId);
				const filters = this._getFilters(layerId, outputEdgeFilterExceptions, excludeNonProjectedRangeFilters);
				if (filters && filters.length) {
					specObject.spec.filters = filters;
				}

				/**
				 * @todo: useAPI.  should check for empty queryHints.
				 * but for now we reserve the same behavior as current that it includes empty queryHints.
				 * if (_.keys(queryHints).length) { specObject.spec.queryHints = queryHints; }
				 */
				specObject.spec.queryHints = queryHints;
				const suppression = this._getSuppression();
				if (suppression) {
					specObject.spec.suppress = [suppression];
				}

				querySpecList.push(specObject);
			});
			return querySpecList;
		}

		_findDataItemInSlotsById(slots, id) {
			let _dataItem;
			slots.forEach(slot=>{
				slot.getDataItemList().forEach(dataItem=>{
					if (dataItem.getId() === id) {
						_dataItem = dataItem;
					}
				});
			});
			return _dataItem;
		}
		_getMinMaxQuerySpecList(layerToQuerySlotsMap, mainQuerySpecList) {
			const minMaxQuerySpecList = [];
			const shouldAxisScaleBeFixed = this.content.getPropertyValue('maintainAxisScales');
			layerToQuerySlotsMap.forEach((querySlots, layerId)=>{
				const containsCategorySlots = !!querySlots.find(slot=>slot.getDataItemList()[0].getType() === 'attribute');
				if (shouldAxisScaleBeFixed && containsCategorySlots) {
					const mainQuerySpecObj =  mainQuerySpecList.find(obj => obj.id === layerId);
					const projectionsAndDataItems = {
						dataItems: mainQuerySpecObj.spec.dataItems,
						limit: mainQuerySpecObj.spec.limit
					};

					let minMaxProjections = JSON.parse(JSON.stringify(mainQuerySpecObj.spec.projections));
					minMaxProjections = minMaxProjections.filter(id=>{
						let dataItem = this._findDataItemInSlotsById(querySlots, id);
						return (dataItem && dataItem.getType() === 'fact');
					});

					// No need for a minmax query when no measures are projected
					if (minMaxProjections && minMaxProjections.length) {
						projectionsAndDataItems.projections = minMaxProjections;

						let aItemsToExclude = [];
						querySlots.forEach(slot=>{
							if (slot.getDefinition().getProperty('useInTopBottomQueries') === false) {
								aItemsToExclude = aItemsToExclude.concat(slot.getDataItemList().map(dataItem=>dataItem.getId()));
							} else if (slot.isStacked()) {
								// If the slot is stacked and has multiple data items then we can not add it to the min max query
								// In this scenario the slot is shown as a nested set of data items and we need to remove using the id.
								aItemsToExclude = aItemsToExclude.concat(slot.getId());
							}
						});
						// Make one query to get min and max only with the fields from the current widget, plus the global filters and slicers (filters from other widgets).
						let topBottomQuery = CommonQueryBuilder.buildMinMaxQueryFromDataItems(projectionsAndDataItems, {
							'itemsToExclude': aItemsToExclude
						}, this.dashboard.getGlassCoreSvc('.Logger'));

						minMaxQuerySpecList.push({
							id: layerId,
							type: 'minmax',
							spec: topBottomQuery
						});
					}
				}
			});
			return minMaxQuerySpecList;
		}

		_canQuery(slot) {
			// If the slot has a multimeasure series item and
			// it is the one and only item in the slot, we can't generate a query from the slot.
			return !(SlotAPIHelper.isMultiMeasuresSeriesSlot(slot) && !slot.isStacked());
		}

		_isSlotWithStackedProjection(slot) {
			// if the slot contains at least 2 items(except multi-measure) and it is stacked(slotDefinition)
			const validDataItems = SlotAPIHelper.getValidDataItems(slot);
			return (slot.isStacked() && validDataItems.length > 1);
		}

		/**
		 * return the projections for the specified slot
		 * @param {*} querySlot queriable slot
		 */
		_getProjectionsForSlot(querySlot) {
			if (this._isSlotWithStackedProjection(querySlot)){
				return [querySlot.getId()];
			} else {
				const validDataItems = SlotAPIHelper.getValidDataItems(querySlot);
				return validDataItems.map(dataItem=>dataItem.getId());
			}
		}

		/**
		 * return the dataItems for the specified slot
		 * @param {*} querySlot queriable slot
		 * @param {*} queryDataItemBuilder
		 */
		_getDataItemsForSlot(querySlot, queryDataItemBuilder, outputEdgeFilterExceptions) {
			const validDataItems = SlotAPIHelper.getValidDataItems(querySlot);
			const dataItemQuerySpecs =  validDataItems.map((dataItem)=>{
				const itemSpec = queryDataItemBuilder.build(dataItem, querySlot, outputEdgeFilterExceptions);
				return itemSpec;
			});

			if (this._isSlotWithStackedProjection(querySlot)) {
				dataItemQuerySpecs.push({
					id: querySlot.getId(),
					nest: validDataItems.map(item=>item.getId())
				});
			}
			return dataItemQuerySpecs;
		}

		_getQueryDataItemBuilder(layerToQuerySlotsMap) {
			const internalVisualization = this.content.getFeature('Visualization.internal');
			const dataItemList = internalVisualization.getSlots().getDataItemList();
			const applyServerSort = dataItemList && _.find(dataItemList, dataItem => dataItem.getColumnId() === '_multiMeasuresSeries') !== undefined;
			return new QueryDataItemBuilder({
				internalVisualization,
				layerToQuerySlotsMap,
				applyServerSort
			});
		}

		_getFilters(layerId, outputEdgeFilterExceptions, excludeNonProjectedRangeFilters) {
			const internalVisualization = this.content.getFeature('Visualization.internal');
			const localFilterList = internalVisualization.getLocalFilters().getFilterList();
			const updatedFilterSpecList = DataQueryUtils.convertExcludeEmptyValuesFilters(localFilterList, internalVisualization.getDataSource());

			const searchFilters = internalVisualization.getSearchFilterList();
			const filterSpec = new FilterQuerySpec(internalVisualization);

			const hasSharedSlot = !!internalVisualization.getDefinition().getSlotList().find(slot => slot.getDatasetIdList().length > 1);
			filterSpec.addFiltersToSpec(updatedFilterSpecList, {
				layerId: layerId,
				edgeFilterExceptions: outputEdgeFilterExceptions,
				excludeNonProjectedRangeFilters: excludeNonProjectedRangeFilters,
				projectInAllLayers: hasSharedSlot
			});

			filterSpec.addFiltersToSpec(searchFilters, {
				layerId: layerId
			});

			return filterSpec.hasFilterSpec() ? filterSpec.getFilterSpec() : null;
		}

		/**
		 * Turn on multiEdgeSort based on following rules:
		 * 1: if slot is isMultiMeasuresSeries and is Rank, does not consider as edge
		 * 2: if slot is isMultiMeasuresSeries and is stacked, considered as one edge
		 * 3: if slot is stacked, it is considered as one edge
		 * 4: if slot is of type ordinal, does not consider as edge
		 * if number of edges is more than one, then turn on multiEdgeSort otherwise not
		 * @return {boolean} true if number of edges to sort is more than 1.
		 * @private
		 */
		_isMultiEdgeSortNeeded(layerToQuerySlotsMap) {
			const visualization = this.content.getFeature('Visualization');
			if (!visualization.getDefinition().getMultiEdgeSort()) {
				return false;
			}
			let numberOfEdges = 0;
			layerToQuerySlotsMap.forEach((querySlots)=>{
				querySlots.forEach(slot=>{
					const dataItems = slot.getDataItemList();
					if (dataItems.length > 0 && dataItems[0].getType() !== 'fact') {
						numberOfEdges++;
					}
				});
			});
			return numberOfEdges > 1;
		}

		_getSuppression() {
			const suppression = this.content.getPropertyValue('suppression');
			if (suppression === QueryProvider.SUPPRESSION_NONE) {
				return QueryProvider.SUPPRESSION_NONE;
			}
		}

		_getQueryHints(layerToQuerySlotsMap) {
			let queryHints = {};
			const suppression = this._getSuppression();
			if (suppression) {
				queryHints.suppress = suppression;
			}

			if (this._isMultiEdgeSortNeeded(layerToQuerySlotsMap)) {
				queryHints.multiEdgeSort = true;
			}

			const refreshProperty = this.content.getPropertyValue('queryRefresh');
			if (refreshProperty && refreshProperty.autoRefresh) {
				// Set the dataCacheExpiry to 40% of the refresh time
				const refreshValue = refreshProperty.value;
				let dataCacheExpiryValue;
				switch (refreshProperty.unit) {
					case 'seconds':
						dataCacheExpiryValue = parseInt(refreshValue * 4 / 10) || 1;
						break;
					case 'minutes':
						dataCacheExpiryValue = parseInt(refreshValue * 60 * 4 / 10) || 1;
						break;
					case 'hours':
						dataCacheExpiryValue = parseInt(refreshValue * 3600 * 4 / 10) || 1;
						break;
					default:
						dataCacheExpiryValue = 1;
				}
				queryHints.dataCacheExpiry = dataCacheExpiryValue.toString();
				queryHints.autoRefreshTime = `${Date.now()}`;
			}
			return queryHints;

		}
	}
	return QueryProvider;
});
